import { CheckCircle2, CircleDashed, Layers, Loader2, TriangleAlert, XCircle } from 'lucide-react'

import { MoneyDisplay } from '@/components/money-display'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { formatBusinessDate, formatShortDate } from '@/lib/datetime'
import { resolveErrorMessage } from '@/lib/error-messages'
import type { Pesewas } from '@/lib/money'

import { COLUMN_SHORT_LABELS, type CollectionColumn } from './constants'
import type { SheetTotals } from './sheet-model'
import type { BatchSummary, LineResult, PreparedLine } from './use-batch-poster'

/**
 * Committing a sheet, and watching it commit.
 *
 * The dialog states the honest thing about this API before anything is sent: there is no endpoint
 * that takes a sheet, so a batch is a run of individual postings and a refusal part-way leaves the
 * earlier lines posted. That is not a caveat buried in a tooltip — it changes what an operator does
 * when a line fails, and they have to know it before they press the button, not after.
 *
 * While the run is going the same list stays on screen with each line's outcome as it settles, so
 * a batch that stops half-way is legible rather than a spinner that ended badly.
 */
export function PostBatchDialog({
  open,
  onOpenChange,
  lines,
  totals,
  columns,
  declaredMinor,
  valueDate,
  businessDate,
  results,
  activeKey,
  isPosting,
  summary,
  onConfirm,
  onRetryRefused,
  onFinish,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  lines: PreparedLine[]
  totals: SheetTotals
  columns: CollectionColumn[]
  declaredMinor: Pesewas | null
  valueDate: string
  businessDate: string
  results: Record<string, LineResult>
  activeKey: string | null
  isPosting: boolean
  summary: BatchSummary | null
  onConfirm: () => void
  onRetryRefused: () => void
  onFinish: () => void
}) {
  const varianceMinor = declaredMinor === null ? null : declaredMinor - totals.capturedMinor
  const refusedCount = summary?.refusedCount ?? 0

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // A run in flight is not abandoned by pressing Escape. Every line already sent has posted,
        // and closing the window over the top of it hides which ones.
        if (isPosting) {
          return
        }
        onOpenChange(next)
      }}
    >
      <DialogContent className="max-w-2xl" showCloseButton={!isPosting}>
        <DialogHeader>
          <DialogTitle>
            {summary === null ? 'Post this collection sheet' : 'What posted'}
          </DialogTitle>
          <DialogDescription>
            {lines.length} line{lines.length === 1 ? '' : 's'}, value dated{' '}
            {formatShortDate(valueDate)}, landing on the branch day{' '}
            {formatBusinessDate(businessDate)}.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="max-h-[26rem] space-y-3 overflow-y-auto">
          {summary === null ? (
            <>
              <dl className="grid grid-cols-2 gap-3 rounded-card border border-border-subtle bg-surface-muted p-3 sm:grid-cols-4">
                {columns.map((column) => (
                  <div key={column}>
                    <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">
                      {COLUMN_SHORT_LABELS[column]}
                    </dt>
                    <dd>
                      <MoneyDisplay value={totals.byColumn[column]} className="text-sm" />
                    </dd>
                  </div>
                ))}
                <div>
                  <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">
                    Total
                  </dt>
                  <dd>
                    <MoneyDisplay value={totals.capturedMinor} emphasis="strong" className="text-sm" />
                  </dd>
                </div>
              </dl>

              {varianceMinor !== null && varianceMinor !== 0 ? (
                <Alert tone="warning">
                  <TriangleAlert aria-hidden />
                  <div className="space-y-1">
                    <AlertTitle>This does not match the officer&apos;s declared total</AlertTitle>
                    <AlertDescription>
                      The sheet declares{' '}
                      <MoneyDisplay value={declaredMinor} className="inline" /> and{' '}
                      <MoneyDisplay value={totals.capturedMinor} className="inline" /> has been
                      keyed, a difference of{' '}
                      <MoneyDisplay value={varianceMinor} signed className="inline" />. Posting
                      commits the keyed figures. The difference is not recorded anywhere in this
                      system and has to be raised with the officer on paper.
                    </AlertDescription>
                  </div>
                </Alert>
              ) : null}

              <Alert tone="info">
                <Layers aria-hidden />
                <div className="space-y-1">
                  <AlertTitle>These post one at a time, not as one transaction</AlertTitle>
                  <AlertDescription>
                    This API has no endpoint that takes a whole sheet: each line is sent to the
                    module that owns the money. If one is refused, the lines before it stay posted
                    and the run carries on to the rest. Every line carries an idempotency key, so
                    re-running the sheet afterwards re-posts nothing that already landed.
                  </AlertDescription>
                </div>
              </Alert>
            </>
          ) : (
            <BatchOutcome summary={summary} />
          )}

          <ul className="divide-y divide-border-subtle rounded-card border border-border-subtle">
            {lines.map((line) => (
              <li key={line.key} className="flex items-center gap-3 px-3 py-2">
                <LineIcon result={results[line.key]} active={activeKey === line.key} />

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-heading">
                    <span className="numeric mr-1.5 text-2xs text-muted tabular-nums">
                      {line.lineNumber}
                    </span>
                    {line.memberName}
                  </p>
                  <p className="truncate text-2xs text-muted">
                    {COLUMN_SHORT_LABELS[line.column]} ·{' '}
                    <span className="font-mono">{line.targetReference}</span>
                  </p>
                </div>

                <div className="shrink-0 text-right">
                  <MoneyDisplay value={line.amountMinor} className="text-sm" />
                  <LineNote result={results[line.key]} keyedMinor={line.amountMinor} />
                </div>
              </li>
            ))}
          </ul>
        </DialogBody>

        <DialogFooter>
          {summary === null ? (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isPosting}>
                Back to the sheet
              </Button>
              <Button variant="primary" onClick={onConfirm} disabled={isPosting || lines.length === 0}>
                {isPosting ? 'Posting…' : `Post ${String(lines.length)} lines`}
              </Button>
            </>
          ) : (
            <>
              {refusedCount > 0 ? (
                <Button variant="secondary" onClick={onRetryRefused} disabled={isPosting}>
                  Retry the {refusedCount} refused line{refusedCount === 1 ? '' : 's'}
                </Button>
              ) : null}
              <Button variant="primary" onClick={onFinish} disabled={isPosting}>
                Done
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function BatchOutcome({ summary }: { summary: BatchSummary }) {
  const clean = summary.refusedCount === 0

  return (
    <Alert tone={clean ? 'success' : 'warning'}>
      {clean ? <CheckCircle2 aria-hidden /> : <TriangleAlert aria-hidden />}
      <div className="space-y-1">
        <AlertTitle>
          {clean
            ? `${String(summary.postedCount + summary.replayedCount)} lines posted`
            : `${String(summary.postedCount + summary.replayedCount)} posted, ${String(summary.refusedCount)} refused`}
        </AlertTitle>
        <AlertDescription>
          <MoneyDisplay value={summary.postedMinor} className="inline" /> reached the ledger.
          {summary.replayedCount > 0 ? (
            <>
              {' '}
              {summary.replayedCount} of these had already been posted under the same operation id
              and were not taken a second time.
            </>
          ) : null}
          {clean
            ? ' The lines are traceable to this sheet through the meeting reference on each repayment and the narration on each savings posting.'
            : ' The refused lines changed nothing. Fix what each one names and retry them — the lines that posted will not post again.'}
        </AlertDescription>
      </div>
    </Alert>
  )
}

function LineIcon({ result, active }: { result: LineResult | undefined; active: boolean }) {
  if (active || result?.status === 'posting') {
    return <Loader2 className="size-4 shrink-0 animate-spin text-brand-text" aria-label="Posting" />
  }

  if (result?.status === 'posted') {
    return <CheckCircle2 className="size-4 shrink-0 text-success-text" aria-label="Posted" />
  }

  if (result?.status === 'refused') {
    return <XCircle className="size-4 shrink-0 text-danger-text" aria-label="Refused" />
  }

  return <CircleDashed className="size-4 shrink-0 text-muted" aria-label="Waiting" />
}

function LineNote({
  result,
  keyedMinor,
}: {
  result: LineResult | undefined
  keyedMinor: Pesewas
}) {
  if (result?.status === 'posted') {
    // A replay answers with the amount that was originally posted, which is not necessarily the
    // amount now on screen. Where they differ, the ledger's figure is the true one and saying so
    // is the whole reason the comparison is made.
    const mismatched = result.postedAmountMinor !== keyedMinor

    return (
      <p className={mismatched ? 'text-2xs text-warning-text' : 'text-2xs text-success-text'}>
        {mismatched ? (
          <>
            Ledger took <MoneyDisplay value={result.postedAmountMinor} className="inline" />
          </>
        ) : result.wasReplay ? (
          'Already posted'
        ) : (
          'Posted'
        )}
      </p>
    )
  }

  if (result?.status === 'refused') {
    return <p className="max-w-56 text-2xs text-danger-text">{resolveErrorMessage(result.error)}</p>
  }

  return null
}
